import {
  useReducer,
  useContext,
  createContext,
  useEffect,
  useCallback,
} from "react";
import { useAuth } from "./AuthContext";

const baseUrl = process.env.REACT_APP_BASE_URL;

const StoriesContext = createContext();

const initialState = {
  stories: [],
  storyIsLoading: false,
  currentStory: null,
  error: null,
};

function reducer(state, action) {
  switch (action.type) {
    case "loading":
      return { ...state, storyIsLoading: true, error: null };
    case "stories/loaded":
      const sortedStories = (action.payload || []).sort(
        (a, b) => new Date(b.StartDate) - new Date(a.StartDate),
      );
      return {
        ...state,
        storyIsLoading: false,
        stories: sortedStories,
        error: null,
      };
    case "story/loaded":
      if (
        state.currentStory?.StoryID === action.payload?.StoryID &&
        JSON.stringify(state.currentStory) === JSON.stringify(action.payload)
      ) {
        return state;
      }
      return {
        ...state,
        storyIsLoading: false,
        currentStory: action.payload || null,
        error: null,
      };
    case "story/created":
      const newStories = [...state.stories, action.payload].sort(
        (a, b) => new Date(b.StartDate) - new Date(a.StartDate),
      );
      return {
        ...state,
        stories: newStories,
        currentStory: action.payload,
        error: null,
        storyIsLoading: false,
      };
    case "story/updated":
      const updatedStories = state.stories
        .map((story) =>
          story.StoryID === action.payload.StoryID ? action.payload : story,
        )
        .sort((a, b) => new Date(b.StartDate) - new Date(a.StartDate));
      return {
        ...state,
        storyIsLoading: false,
        stories: updatedStories,
        currentStory: action.payload,
        error: null,
      };
    case "story/deleted":
      const filteredStories = state.stories
        .filter((story) => story.StoryID !== action.payload)
        .sort((a, b) => new Date(b.StartDate) - new Date(a.StartDate));
      return {
        ...state,
        storyIsLoading: false,
        stories: filteredStories,
        currentStory: null,
        error: null,
      };
    case "reset":
      return {
        stories: [],
        storyIsLoading: false,
        currentStory: null,
        error: null,
      };
    case "rejected":
      return { ...state, storyIsLoading: false, error: action.payload };
    default:
      throw new Error(`Unknown action type: ${action.type}`);
  }
}

function StoriesProvider({ children }) {
  const { user, isLoading, fetchWithToken } = useAuth(); // Removed logout
  const [{ stories, storyIsLoading, currentStory, error }, dispatch] =
    useReducer(reducer, initialState);

  useEffect(() => {
    if (isLoading || !user?.CustUUID) {
      console.log("StoriesContext: Waiting for user authentication");
      return;
    }
    console.log("StoriesContext: user:", user);
    console.log("StoriesContext: currentStory:", currentStory);
    console.log("StoriesContext: stories:", stories);
    console.log("StoriesContext: error:", error);
  }, [user, currentStory, stories, error]);

  const fetchStories = useCallback(async () => {
    if (!user?.userId) {
      console.warn("fetchStories: No userId available");
      dispatch({ type: "rejected", payload: "Please log in to view stories" });
      return;
    }
    dispatch({ type: "loading" });
    try {
      const url = `${baseUrl}/stories/?custuuid=${user.userId}`;
      console.log(`fetchStories: Fetching URL=${url}`);
      const res = await fetchWithToken(url);
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || `HTTP ${res.status}`);
      }
      const data = await res.json();
      console.log("fetchStories: Response=", data);
      if (data.status === "error") {
        throw new Error(data.message);
      }
      const storiesWithTags = data.data.map((story) => ({
        ...story,
        StoryTags: story.StoryTags || "",
        StoryLocations: story.StoryLocations || "",
      }));
      dispatch({ type: "stories/loaded", payload: storiesWithTags });
    } catch (err) {
      console.error("fetchStories error:", err.message);
      dispatch({
        type: "rejected",
        payload: err.message || "Error loading stories",
      });
    }
  }, [user, fetchWithToken]);

  useEffect(() => {
    if (user?.userId) {
      fetchStories();
    } else {
      dispatch({ type: "reset" });
    }
  }, [fetchStories, user]);

  const getStory = useCallback(
    async (storyID) => {
      if (!storyID || isNaN(storyID)) {
        console.warn("getStory: Invalid storyID=", storyID);
        dispatch({ type: "rejected", payload: "Invalid story ID" });
        return;
      }
      if (Number(storyID) === currentStory?.StoryID) {
        console.log("getStory: Story already loaded, storyID=", storyID);
        return;
      }
      if (!user?.userId) {
        console.warn("getStory: No userId available");
        dispatch({ type: "rejected", payload: "Please log in to view story" });
        return;
      }
      dispatch({ type: "loading" });
      try {
        const url = `${baseUrl}/stories/${storyID}/${user.userId}`;
        console.log("getStory: Fetching URL=", url);
        console.log("getStory: userId=", user.userId);
        const res = await fetchWithToken(url);
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.message || `HTTP ${res.status}`);
        }
        const data = await res.json();
        console.log("getStory: Response=", data);
        if (data.status === "error") {
          throw new Error(data.message);
        }
        const storyWithTags = {
          ...data.data,
          StoryTags: data.data.StoryTags || "",
          StoryLocations: data.data.StoryLocations || "",
        };
        dispatch({ type: "story/loaded", payload: storyWithTags });
      } catch (err) {
        console.error("getStory error:", err.message);
        dispatch({
          type: "rejected",
          payload: err.message || "Error loading story",
        });
        dispatch({ type: "story/loaded", payload: null });
      }
    },
    [user, fetchWithToken, currentStory?.StoryID],
  );

  const createStory = useCallback(
    async (newStory) => {
      if (!user?.userId) {
        throw new Error("Please log in to create a story");
      }
      dispatch({ type: "loading" });
      try {
        console.log("createStory: Creating story:", newStory);
        const res = await fetchWithToken(`${baseUrl}/stories/`, {
          method: "POST",
          body: JSON.stringify(newStory),
        });
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.message || `HTTP ${res.status}`);
        }
        const data = await res.json();
        console.log("createStory: Response=", data);
        if (data.status === "error") {
          throw new Error(data.message);
        }
        const storyWithTags = {
          ...data.data,
          StoryTags: data.data.StoryTags || "",
          StoryLocations: data.data.StoryLocations || "",
        };
        dispatch({ type: "story/created", payload: storyWithTags });
        await fetchStories();
        return { storyId: data.data.StoryID, ...data.data };
      } catch (err) {
        console.error("createStory error:", err.message);
        dispatch({
          type: "rejected",
          payload: err.message || "Error creating story",
        });
        throw new Error(err.message || "Error creating story");
      }
    },
    [user, fetchWithToken, fetchStories],
  );

  const updateStory = useCallback(
    async (theStory, storyID) => {
      if (!user?.userId) {
        dispatch({
          type: "rejected",
          payload: "Please log in to update story",
        });
        return;
      }
      dispatch({ type: "loading" });
      try {
        console.log("updateStory: Updating storyID=", storyID);
        console.log("updateStory: Tags Received=", theStory.StoryTags);
        const res = await fetchWithToken(
          `${baseUrl}/stories/${storyID}/${user.userId}`,
          {
            method: "PUT",
            body: JSON.stringify(theStory),
          },
        );
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.message || `HTTP ${res.status}`);
        }
        const data = await res.json();
        console.log("updateStory: Response=", data);
        if (data.status === "error") {
          throw new Error(data.message);
        }
        const storyWithTags = {
          ...data.data,
          StoryTags: data.data.StoryTags || "",
          StoryLocations: data.data.StoryLocations || "",
        };
        dispatch({ type: "story/updated", payload: storyWithTags });
      } catch (err) {
        console.error("updateStory error:", err.message);
        dispatch({
          type: "rejected",
          payload: err.message || "Error updating story",
        });
      }
    },
    [user, fetchWithToken],
  );

  const deleteStory = useCallback(
    async (storyID) => {
      if (!user?.userId) {
        dispatch({
          type: "rejected",
          payload: "Please log in to delete story",
        });
        return;
      }
      dispatch({ type: "loading" });
      try {
        console.log("deleteStory: Deleting storyID=", storyID);
        const res = await fetchWithToken(
          `${baseUrl}/stories/${storyID}/${user.userId}`,
          {
            method: "DELETE",
          },
        );
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.message || `HTTP ${res.status}`);
        }
        const data = await res.json();
        console.log("deleteStory: Response=", data);
        if (data.status === "error") {
          throw new Error(data.message);
        }
        dispatch({ type: "story/deleted", payload: storyID });
      } catch (err) {
        console.error("deleteStory error:", err.message);
        dispatch({
          type: "rejected",
          payload: err.message || "Error deleting story",
        });
      }
    },
    [user, fetchWithToken],
  );

  const resetStories = useCallback(() => {
    dispatch({ type: "reset" });
  }, []);

  return (
    <StoriesContext.Provider
      value={{
        stories,
        storyIsLoading,
        currentStory,
        error,
        getStory,
        createStory,
        updateStory,
        deleteStory,
        resetStories,
        fetchStories,
      }}
    >
      {children}
    </StoriesContext.Provider>
  );
}

function useStories() {
  const context = useContext(StoriesContext);
  if (context === undefined) {
    throw new Error("StoriesContext used outside of StoriesProvider");
  }
  return context;
}

export { StoriesProvider, useStories };
